/* Wrapper for the whole videochat subsystem - sets up the video context, gets a token, and passes the token to its children (which it doesn't render until it has them) */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { VideoAlphaWarning } from "./VideoAlphaWarning";
import { connect, Room, Participant, TrackPublication, LocalParticipant, LocalVideoTrack, ConnectOptions, createLocalAudioTrack, LocalAudioTrack, LocalTrack} from "twilio-video";
import PropTypes from "prop-types";
import IconButton from "./IconButton";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons";

export interface IClickToTalkProps {
    localAudioTrack?: LocalAudioTrack | null;
}

const TrackView = ({ track, alwaysMuted }) => {
    const ref = useRef<HTMLVideoElement>(null!);
    useEffect(() => {
        track.attach(ref.current);
        ref.current.muted = alwaysMuted;
        const attachedDomNode = ref.current; //stash this because it may have changed by the time we run the cleanup
        return () => {
            track.detach(attachedDomNode);
        };
    }, [track, alwaysMuted]);
    if (track.kind === "video") {
        return <video ref={ref}/>
    } else {
        return <audio ref={ref}/>
    }
}


/* Keeps track of a Publication's "track", and renders it */
const PublicationView = ({ publication, alwaysMuted }) => {
    const [track, setTrack] = useState(publication && publication.track);
    useEffect(() => {
        setTrack(publication && publication.track);
        if (publication) {
            const removeTrack = () => setTrack(null);
            publication.on('subscribed', setTrack);
            publication.on('unsubscribed', removeTrack);
            return () => {
                publication.off('subscribed', setTrack);
                publication.off('unsubscribed', removeTrack);
            };
        }
    }, [publication]);

    return (track ? <TrackView track={track} alwaysMuted={alwaysMuted} /> : <div>No track</div>);
}
PublicationView.propTypes = {
    publication: PropTypes.object.isRequired
}


/* Handed a Participant. Responsible for getting, and keeping track of, that Participant's publications */
const ParticipantView = (props) => {
    const [publications, setPublications] = useState<Array<TrackPublication>>([]);
    useEffect(() => {
        setPublications(Array.from(props.participant.tracks.values()) as TrackPublication[]);
        const publicationAdded = (publication: TrackPublication) => setPublications(prevPublications => [...prevPublications, publication]);
        const publicationRemoved = (publication: TrackPublication) => setPublications(prevPublications => prevPublications.filter(p => p !== publication));
        props.participant.on('trackPublished', publicationAdded);
        props.participant.on('trackUnpublished', publicationRemoved);
        return () => {
            props.participant.off('trackPublished', publicationAdded);
            props.participant.off('trackUnpublished', publicationRemoved);
        };
    }, [props.participant])


    return (
        <>
            {publications.map((publication) => <PublicationView key={publication.trackSid} publication={publication} alwaysMuted={props.alwaysMuted} />)}
        </>
    );
}



const LocalVideoDiv = styled.div`
    position:absolute;
    top:0;
    right:0;
    width:50px;
    border:2px solid grey;
    text-align:center;
    font-size:60%;
`;

const VideoParticipantsDiv = styled.div`
width: 100%;
display: grid;
min-height:20px;
grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
`;

const VideoParticipantDiv = styled.div`
overflow:hidden;
`;



/* TODO: Right now this is toggle but actually we probably want mousedown=talk, mouseup=mute. + Maybe also keyboard shortcut? */
export const ClickToTalk = (props : IClickToTalkProps) => {
    const [micIsLive, setMicIsLive] = useState(false);

    const switchLocalAudioTrack = (isEnabled : boolean) => {
        console.log("Set mic to ",isEnabled);
        if (isEnabled === true) {
            if (props.localAudioTrack) props.localAudioTrack.enable();
        } else {
            if (props.localAudioTrack) props.localAudioTrack.disable();
        }
    }

    const turnMicOff = () => {
        setMicIsLive(false);
        switchLocalAudioTrack(false);
    }

    const turnMicOn = () => {
        setMicIsLive(true);
        switchLocalAudioTrack(true);
    }

    return (<IconButton icon={faMicrophone} highlighted={micIsLive} onMouseDown={turnMicOn} onMouseUp={turnMicOff}/>);

}


// eslint-disable-next-line no-unused-vars
export const VideoChat = (props) => {

    const [room, setRoom] = useState<Room | undefined>(undefined);
    const [participants, setParticipants] = useState<Array<Participant>>([]);
    const [localParticipant, setlocalParticipant] = useState<LocalParticipant | undefined>(undefined);
    const [localAudioTrack, setLocalAudioTrack] = useState<LocalAudioTrack | null>(null); 

    useEffect(() => {

        async function doAsyncConnect() {
            console.log("Getting token");
            const response = await fetch(".netlify/functions/twilioToken?roomKey=" + encodeURI(props.roomKey) + "&nickName=" + encodeURI(props.nickName));
            const data = await response.json();
            console.log("Connecting with...", data.token);

            /* Now grab a LocalVideo in some kind of safe way (ie don't explode if no camera) */
            /* This is kind of weird. We return it from the function because we need it now (setState is async), but we *also* setState so that we can use it in a component later. */
            const myLocalVideoTrack:LocalVideoTrack | undefined = await safelyGrabVideo();
            const myLocalAudioTrack:LocalAudioTrack | undefined = await safelyGrabAudio();

            var connectOptions : ConnectOptions = {audio: false, video: false, tracks: [], maxAudioBitrate: 200000, maxVideoBitrate: 150000};  /* 150kbps video max should be OK for groups of 6-ish even on bad connections */
            var tracks: Array<LocalTrack> = [];
            if (myLocalVideoTrack) {tracks.push(myLocalVideoTrack)};
            if (myLocalAudioTrack) {tracks.push(myLocalAudioTrack)};
            connectOptions.tracks = tracks;

            var theRoom = await connect(data.token, connectOptions);
            // var theRoom = await connect(data.token);

            console.log("Connected, room is", theRoom);
            if (!theRoom) { return }
            setupRoom(theRoom);
            setRoom(theRoom);
        }

        async function safelyGrabVideo(): Promise<LocalVideoTrack | undefined> {
            try {
                console.log("Getting video");
                 /* Square so that we effectively constrain largest dimension since we don't know the aspect ratio. Force low resolution because we're only showing
                 thumbnails anyway and we're in P2P mode to save money so we want to conserve upstream bandwidth. */
                const constraints = {
                    audio: false,
                    video: {width: 384, height: 384 }
                }
                const stream: MediaStream = await navigator.mediaDevices.getUserMedia(constraints);
                return new LocalVideoTrack(stream.getVideoTracks()[0]); 
            } catch(e) {
                if (e.name === "NotFoundError" || e.name === "AbortError" || e.name === "NotReadableError") {
                    console.log("Looks like you don't have a camera");
                    return undefined;
                } else {
                    console.log("Got error",e.name);
                    throw(e);
                }
            }
        }

        async function safelyGrabAudio(): Promise<LocalAudioTrack | undefined> {
            try {
                console.log("Getting audio");
                const myLocalAudioTrack = await createLocalAudioTrack();
                setLocalAudioTrack(myLocalAudioTrack);
                myLocalAudioTrack.disable(); //Start muted
                console.log("Got audio");
                return myLocalAudioTrack;
            } catch(e) {
                if (e.name === "NotFoundError" || e.name === "AbortError" || e.name === "NotReadableError") {
                    console.log("Looks like you don't have a microphone");
                    return undefined;
                } else {
                    console.log("Got error",e.name);
                    throw(e);
                }
            }
        }

        function setupRoom(room: Room) {
            console.log("Setuproom, existing particip are", room.participants);
            setParticipants(Array.from(room.participants.values()));

            setlocalParticipant(room.localParticipant);

            room.on('participantConnected', participant => {
                console.log(`A remote Participant connected: ${participant}`);

                setParticipants(prevParticipants => ([...prevParticipants, participant]));
            });
            room.on('participantDisconnected', participant => {
                console.log(`A remote Participant connected: ${participant}`);
                setParticipants(prevParticipants => (prevParticipants.filter((p) => p !== participant)));
            });
        }

        console.log("Useeffect for videochat");
        if (!room) {
            doAsyncConnect();
        }
        return () => {
            console.log("Teardown for videochat");
            if (room?.state === "connected") {
                console.log("Connected, disconnecting");
                room.disconnect();
            }
        }
    }, [room, props.nickName, props.roomKey]); //If the nickname/roomkey changes, we'll need to get a new token, which means disconnecting and reconnecting, so rerun the effect.

    return (
        <>
            <VideoAlphaWarning/>
            <ClickToTalk localAudioTrack={localAudioTrack}/>
            <VideoParticipantsDiv>
                <LocalVideoDiv>{localParticipant ? <ParticipantView participant={localParticipant} alwaysMuted={true} /> : <div>No camera</div>}</LocalVideoDiv>
                {/* We show other participants video even if you don't have a camera, because you may have the video display on a different device to the camera (eg second-screen mode) */}
                {participants.map((p) => <VideoParticipantDiv key={p.sid + "-1"}><ParticipantView participant={p} alwaysMuted = {false} /></VideoParticipantDiv>)}
            </VideoParticipantsDiv>
        </>

    );
};




